import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import LocalStorage from "../Helpers/LocalStorage";
import { useTranslate } from "@tolgee/react";

const POLL_INTERVAL = 3000;
const MAX_VIDEO_BYTES = 5 * 1024 ** 3;
const API_URL = process.env.REACT_APP_API_URL;
const MAX_GLOSSARY_ENTRIES = 200;

const authHeaders = () => ({
  Authorization: `Bearer ${LocalStorage.getAuthenticationToken()}`,
});

// Persists state across SPA navigation within the same tab
let _persistedJobs = [];

const useVideoTranslation = (apiProjectId) => {
  const { t } = useTranslate();
  const [jobs, setJobsRaw] = useState(_persistedJobs);
  const pollRefs = useRef({});

  // Wrap setter to keep module-level state in sync
  const setJobs = useCallback((updater) => {
    setJobsRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      _persistedJobs = next;
      return next;
    });
  }, []);

  const clearPoll = useCallback((jobId) => {
    if (pollRefs.current[jobId]) {
      clearInterval(pollRefs.current[jobId]);
      delete pollRefs.current[jobId];
    }
  }, []);

  useEffect(() => {
    return () => {
      Object.values(pollRefs.current).forEach(clearInterval);
      pollRefs.current = {};
    };
  }, []);

  const updateJob = useCallback((localId, patch) => {
    setJobs((prev) => prev.map((j) => (j.id === localId ? { ...j, ...patch } : j)));
  }, [setJobs]);

  const pollJob = useCallback((localId, jobId, projectId) => {
    if (pollRefs.current[jobId]) return;
    pollRefs.current[jobId] = setInterval(async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/v1/connector/video_translations/${jobId}`,
          { headers: authHeaders(), params: projectId ? { project_id: projectId } : undefined }
        );
        updateJob(localId, { status: data.status });
        if (data.status === "completed") {
          clearPoll(jobId);
          updateJob(localId, { download_url: data.download_url, srt_download_url: data.srt_download_url || null });
        } else if (data.status === "failed" || data.status === "rejected") {
          clearPoll(jobId);
          updateJob(localId, { error_message: data.error_message || "Translation failed" });
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          clearPoll(jobId);
        } else if (status === 404) {
          clearPoll(jobId);
          updateJob(localId, { status: "failed", error_message: t("video_tool.status_failed") });
        }
      }
    }, POLL_INTERVAL);
  }, [updateJob, clearPoll, t]);

  // On mount, resume polling for any in-flight jobs from persisted state
  useEffect(() => {
    _persistedJobs
      .filter((j) => j.job_id && !["completed", "failed", "rejected"].includes(j.status))
      .forEach((j) => pollJob(j.id, j.job_id, j.project_id));
  }, [pollJob]);

  const startTranslation = useCallback(
    async (file, sourceLang, targetLangs, includeSubtitles = false, includeSrtExport = false, videoContext = "", glossary = [], voiceMode = "clone") => {
      const baseId = `${Date.now()}-${Math.random()}`;
      const entries = targetLangs.map((targetLang) => ({
        id: `${baseId}-${targetLang}`,
        job_id: null,
        project_id: apiProjectId || null,
        filename: file.name,
        sourceLang,
        targetLang,
        status: "uploading",
        download_url: null,
        error_message: null,
      }));
      const failAll = (message) => setJobs((prev) => prev.map((j) => ({ ...j, status: "failed", error_message: message })));

      if (!apiProjectId) {
        setJobs(entries.map((j) => ({ ...j, status: "failed", error_message: t("video_tool.error_no_project") })));
        return;
      }

      if (file.size > MAX_VIDEO_BYTES) {
        setJobs(entries.map((j) => ({
          ...j,
          status: "failed",
          error_message: t("video_tool.error_file_too_large", "File size exceeded: maximum video size is 5 GB"),
        })));
        return;
      }

      setJobs(entries);

      try {
        const params = { filename: file.name, file_size: file.size, source_language: sourceLang, target_languages: targetLangs, voice_mode: voiceMode };
        if (apiProjectId) params.project_id = apiProjectId;
        if (includeSubtitles) params.include_subtitles = true;
        if (includeSrtExport) params.include_srt_export = true;

        const trimmedContext = (videoContext || "").trim();
        if (trimmedContext) params.video_context = trimmedContext;

        const glossaryEntries = {};
        (glossary || []).slice(0, MAX_GLOSSARY_ENTRIES).forEach(({ term, translation }) => {
          const trimmedTerm = (term || "").trim();
          const trimmedTranslation = (translation || "").trim();
          if (trimmedTerm && trimmedTranslation) glossaryEntries[trimmedTerm] = trimmedTranslation;
        });
        if (Object.keys(glossaryEntries).length > 0) params.glossary = glossaryEntries;

        const { data: created } = await axios.post(
          `${API_URL}/api/v1/connector/video_translations`,
          params,
          { headers: authHeaders() }
        );

        const createdJobs = created.jobs || [{ job_id: created.job_id, target_language: created.target_language }];
        const jobIdFor = Object.fromEntries(createdJobs.map((j) => [j.target_language, j.job_id]));
        entries.forEach((entry) => updateJob(entry.id, { job_id: jobIdFor[entry.targetLang] }));

        const upload = await fetch(created.upload_url, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type || "video/mp4" },
        });
        if (!upload.ok) throw new Error(t("video_tool.error_upload_failed", "Upload failed, please try again"));

        await axios.post(
          `${API_URL}/api/v1/connector/video_translations/${created.job_id}/start`,
          apiProjectId ? { project_id: apiProjectId } : {},
          { headers: authHeaders() }
        );

        entries.forEach((entry) => {
          updateJob(entry.id, { status: "submitted" });
          pollJob(entry.id, jobIdFor[entry.targetLang], apiProjectId);
        });
      } catch (err) {
        const message =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          t("video_tool.error_upload_failed", "Upload failed, please try again");
        failAll(message);
      }
    },
    [apiProjectId, setJobs, updateJob, pollJob, t]
  );

  const reset = useCallback(() => {
    Object.keys(pollRefs.current).forEach(clearPoll);
    setJobs([]);
  }, [clearPoll, setJobs]);

  const downloadJob = useCallback((url) => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  return { jobs, startTranslation, reset, downloadJob };
};

export default useVideoTranslation;
